// app
import { ngDelegate } from './ng.delegate';
import { utilSvc, validSvc } from '../services/exports';

// types
import { ErrorVM } from '../../../global';

/**
 *    API ERRORS
 *    look at error response, some error codes throw app-wide events
 *    other codes are expected errors like validation, etc
 */

const apiErrorHandler = (r: any, purl: string): ErrorVM | null => {
    // axios fix
    r = r || { status: 503 };

    // response is corrupt, HTTP 520 is server unknown error
    if (!!!r || !validSvc.isNumber(r.status)) {
        return errEvent('resp.error.corrupt', purl, '520', utilSvc.http.safeResponse(r));
    }

    switch (r.status) {
        case -1:
            // connection refused
            // angular returns connection refused as status -1 ERR_CONNECTION_REFUSED
            return errEvent('resp.error.refusedConnection', purl, '-1', utilSvc.http.safeResponse(r));

        case 0:
            // no connection
            // angular returns connection timeout as status 0
            return errEvent('resp.error.noConnection', purl, '0', utilSvc.http.safeResponse(r));

        case 502:
            // no connection
            // angular returns connection timeout as status 0
            return errEvent('resp.error.badgateway', purl, '502', utilSvc.http.safeResponse(r));

        case 400:
            // Bad Request
            // the request is unreadable or corrupted, cannot be parsed
            return errEvent('resp.error.badRequest', purl, '400', utilSvc.http.safeResponse(r));

        case 401:
            // Unauthorized
            // needs login token or token is bad / timed out
            ngDelegate.logout();
            return errEvent('resp.error.noAuth', purl, '401');

        case 403:
            // Forbidden
            // user auth token is valid but they do not have claim rights
            return errEvent('resp.error.forbidden', purl, '403');
        case 404:
            // Not Found
            // The requested resource is not available
            return null;
        case 409:
            // Conflict
            // doesn't need a body
            return null;

        case 422:
            // Unprocessable Entity
            // validation error
            return verifyValidationFormat(r, purl);

        case 423:
            // Locked
            // user is locked out of api server
            ngDelegate.logout('locked');
            return errEvent('resp.error.lockout', purl, '423');

        case 503:
            // 503 Service Unavailable
            // under maintenance, ui will show "under maintenance page"
            return errEvent('resp.error.maintenance', purl, '503');

        case 504:
            // 504 Gateway timeout (email server is having issues)
            // note: would have used 502 but Angular is already using it
            return errEvent('resp.error.bademail', purl, '504');

        default:
            return errEvent('resp.error.unknown', purl, r.status, utilSvc.http.safeResponse(r));
    }
};

function verifyValidationFormat(r, purl): ErrorVM | null {
    /**
     *    422 VALIDATION ERROR FORMATS
     */

    // always need data and data.Entity
    if (!!!r.data && !!!r.data.Entity) {
        return errEvent('resp.error.422.format', purl, '422', 'data and data.Entity required');
    }
    const entity = r.data.Entity;

    // Root and Fields not present, stop validating, this is not a typical 422 response
    if (!Array.isArray(entity.ROOT) || !Array.isArray(entity.Fields)) {
        return errEvent(
            'resp.error.422.format',
            purl,
            '422',
            ' ROOT array and Fields array required: ' + utilSvc.http.safeResponse(r),
        );
    }

    // valid field item objects
    entity.Fields.forEach(f => {
        // check field Name and Rule exist and have a string value
        if (!!!f || !validSvc.isText(f.Name) || !validSvc.isText(f.Rule)) {
            return errEvent(
                'resp.error.422.format',
                purl,
                '422',
                'Field items need a Name and Rule property: ' + utilSvc.http.safeResponse(r),
            );
        }
    });

    // success
    return null;
}

/*

TBD: other errors to handle specifically instead of throwing an unrecoverable error

404 Not Found
End point doesn’t exist to execute any action

5xx Server Error

500 Internal Server Error
Unexpected error in server code not related to client request content
General catch all for GO Server errors

501 Not Implemented
Endpoint is only a stub
No implementation yet but does exist for future

*/

/**
 *    ERR EVENT
 *    format an api response error to throw as event
 *    note: also used by the core.api.service to throw response events
 */

function errEvent(msg: string, stack: string, status: string, detail?: string): ErrorVM {
    // convenience method to create ErrorVM, only 3 props used
    return {
        msg: msg,
        stack: stack || '',
        status: status || '',
        detail: detail || '',
    };
}

export default apiErrorHandler;
